import { useState, useEffect } from 'react';
import { adminApi } from '@/lib/admin';
import { SectionCard, LoadingState, ErrorState } from '@/pages/admin/components/UI';

interface Schedule {
  id: number;
  market_name: string;
  open_time: string;
  close_time: string;
  timezone: string;
  weekend_enabled: boolean;
  enabled: boolean;
  manual_paused: boolean;
  pause_reason: string | null;
  updated_at: string;
}

export default function AdminSchedule() {
  const [schedule, setSchedule] = useState<Schedule | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // 编辑表单状态
  const [openTime, setOpenTime] = useState('07:00');
  const [closeTime, setCloseTime] = useState('02:00');
  const [weekendEnabled, setWeekendEnabled] = useState(false);
  const [enabled, setEnabled] = useState(true);

  // 手动暂停状态
  const [manualPaused, setManualPaused] = useState(false);
  const [pauseReason, setPauseReason] = useState<string | null>(null);
  const [pauseReasonInput, setPauseReasonInput] = useState('');
  const [togglingPause, setTogglingPause] = useState(false);

  // 品类暂停状态
  const [pausedCategories, setPausedCategories] = useState<string[]>([]);
  const [togglingCategory, setTogglingCategory] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await adminApi.getSchedule();
      setSchedule(data);
      if (data) {
        setOpenTime(data.open_time?.slice(0, 5) || '07:00');
        setCloseTime(data.close_time?.slice(0, 5) || '02:00');
        setWeekendEnabled(data.weekend_enabled);
        setEnabled(data.enabled);
        setManualPaused(!!data.manual_paused);
        setPauseReason(data.pause_reason || null);
        setPauseReasonInput(data.pause_reason || '');
        setPausedCategories(data.paused_categories || []);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleTogglePause = async () => {
    if (togglingPause) return;
    setTogglingPause(true);
    try {
      const willPause = !manualPaused;
      const reason = willPause ? pauseReasonInput.trim() || '行情波动过大，暂停交易' : undefined;
      const res = await adminApi.setPause(willPause, reason);
      setManualPaused(res.paused);
      setPauseReason(res.paused ? reason || null : null);
    } catch {
      alert('操作失败，请重试');
    } finally {
      setTogglingPause(false);
    }
  };

  const handleToggleCategory = async (category: string) => {
    if (togglingCategory) return;
    setTogglingCategory(category);
    try {
      const willPause = !pausedCategories.includes(category);
      const res = await adminApi.toggleCategory(category, willPause);
      setPausedCategories(res.pausedCategories || []);
    } catch {
      alert('操作失败，请重试');
    } finally {
      setTogglingCategory(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await adminApi.updateSchedule({
        open_time: openTime + ':00',
        close_time: closeTime + ':00',
        weekend_enabled: weekendEnabled,
        enabled,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      alert('保存失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState onRetry={fetchData} />;

  return (
    <div className="space-y-5 max-w-xl">
      {/* 手动交易开关 */}
      <SectionCard title="交易开关">
        <div className="p-4 md:p-5">
          {/* 状态横幅 */}
          <div
            className={`rounded-xl p-4 md:p-5 flex items-center justify-between ${
              manualPaused ? 'bg-red-50 border border-red-100' : 'bg-green-50 border border-green-100'
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`w-11 h-11 rounded-full flex items-center justify-center ${
                  manualPaused ? 'bg-red-100' : 'bg-green-100'
                }`}
              >
                <span
                  className={`w-5 h-5 flex items-center justify-center ${
                    manualPaused ? 'text-red-500' : 'text-green-600'
                  }`}
                >
                  <i
                    className={`${manualPaused ? 'ri-pause-circle-line' : 'ri-play-circle-line'} text-2xl`}
                  ></i>
                </span>
              </div>
              <div>
                <p className={`text-sm font-bold ${manualPaused ? 'text-red-600' : 'text-green-700'}`}>
                  {manualPaused ? '已暂停交易' : '交易正常进行中'}
                </p>
                <p className={`text-xs mt-0.5 ${manualPaused ? 'text-red-500' : 'text-green-600'}`}>
                  {manualPaused
                    ? `用户当前无法买入或结算${pauseReason ? ` · ${pauseReason}` : ''}`
                    : '用户可正常买入、卖出与结算'}
                </p>
              </div>
            </div>
            <span
              className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                manualPaused ? 'bg-red-500' : 'bg-green-500 animate-pulse'
              }`}
            />
          </div>

          {/* 暂停原因输入 */}
          <div className="mt-4">
            <label className="block text-brown-900 text-sm font-medium mb-1.5">
              暂停原因（可选）
            </label>
            <input
              type="text"
              value={pauseReasonInput}
              onChange={(e) => setPauseReasonInput(e.target.value)}
              placeholder="例如：行情波动过大，暂停交易"
              className="w-full px-3 py-2.5 rounded-lg border border-brown-200 text-brown-900 text-sm focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400"
            />
            <p className="text-brown-400 text-xs mt-1">
              暂停时会在用户端显示此原因，方便说明停盘情况
            </p>
          </div>

          {/* 暂停/恢复按钮 */}
          <button
            onClick={handleTogglePause}
            disabled={togglingPause}
            className={`mt-4 w-full py-3.5 rounded-xl text-white text-sm font-bold flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60 ${
              manualPaused
                ? 'bg-green-500 hover:bg-green-600'
                : 'bg-red-500 hover:bg-red-600'
            }`}
          >
            {togglingPause ? (
              <>
                <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></span>
                处理中...
              </>
            ) : manualPaused ? (
              <>
                <i className="ri-play-fill"></i>
                恢复交易
              </>
            ) : (
              <>
                <i className="ri-pause-fill"></i>
                暂停交易
              </>
            )}
          </button>
        </div>
      </SectionCard>

      {/* 品类交易开关 */}
      <SectionCard title="品类交易开关">
        <div className="p-4 md:p-5 space-y-3">
          {[
            { key: 'gold', name: '黄金' },
            { key: 'silver', name: '白银' },
            { key: 'kiloBar', name: '公斤条' },
          ].map((item) => {
            const isPaused = pausedCategories.includes(item.key);
            const isLoading = togglingCategory === item.key;
            return (
              <div
                key={item.key}
                className={`flex items-center justify-between rounded-xl p-3.5 border transition-colors ${
                  isPaused ? 'bg-red-50 border-red-100' : 'bg-green-50 border-green-100'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center ${
                      isPaused ? 'bg-red-100' : 'bg-green-100'
                    }`}
                  >
                    <i
                      className={`${isPaused ? 'ri-pause-circle-line text-red-500' : 'ri-play-circle-line text-green-600'} text-lg`}
                    ></i>
                  </div>
                  <div>
                    <p className={`text-sm font-bold ${isPaused ? 'text-red-600' : 'text-green-700'}`}>
                      {item.name}
                    </p>
                    <p className={`text-xs mt-0.5 ${isPaused ? 'text-red-500' : 'text-green-600'}`}>
                      {isPaused ? '已暂停交易' : '正常交易中'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleToggleCategory(item.key)}
                  disabled={isLoading}
                  className={`relative w-12 h-7 rounded-full transition-colors cursor-pointer disabled:opacity-60 ${
                    isPaused ? 'bg-red-500' : 'bg-green-500'
                  }`}
                >
                  {isLoading ? (
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin"></span>
                    </span>
                  ) : (
                    <span
                      className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-sm transition-transform ${
                        isPaused ? 'translate-x-0' : 'translate-x-5'
                      }`}
                    />
                  )}
                </button>
              </div>
            );
          })}
          <p className="text-brown-400 text-xs mt-1">
            关闭某一品类后，用户端该品类将显示暂停交易，无法下单
          </p>
        </div>
      </SectionCard>

      {/* 交易时间设置 */}
      <SectionCard title="交易时间设置">
        <div className="p-4 md:p-5 space-y-5">
          {/* 启用开关 */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-brown-900 text-sm font-medium">启用交易时间控制</p>
              <p className="text-brown-400 text-xs mt-0.5">关闭后平台全天开放交易</p>
            </div>
            <button
              onClick={() => setEnabled(!enabled)}
              className={`relative w-12 h-7 rounded-full transition-colors cursor-pointer ${
                enabled ? 'bg-gold-500' : 'bg-brown-200'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-sm transition-transform ${
                  enabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {enabled && (
            <>
              {/* 开盘时间 */}
              <div>
                <label className="block text-brown-900 text-sm font-medium mb-1.5">
                  每日开盘时间
                </label>
                <input
                  type="time"
                  value={openTime}
                  onChange={(e) => setOpenTime(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-brown-200 text-brown-900 text-sm focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400"
                />
                <p className="text-brown-400 text-xs mt-1">格式 HH:MM，例如 07:00</p>
              </div>

              {/* 闭盘时间 */}
              <div>
                <label className="block text-brown-900 text-sm font-medium mb-1.5">
                  每日闭盘时间
                </label>
                <input
                  type="time"
                  value={closeTime}
                  onChange={(e) => setCloseTime(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-brown-200 text-brown-900 text-sm focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400"
                />
                <p className="text-brown-400 text-xs mt-1">
                  支持跨天设置，例如 02:00 表示次日凌晨 2 点闭盘
                </p>
              </div>

              {/* 周末交易 */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-brown-900 text-sm font-medium">周末允许交易</p>
                  <p className="text-brown-400 text-xs mt-0.5">关闭后周六周日全天休市</p>
                </div>
                <button
                  onClick={() => setWeekendEnabled(!weekendEnabled)}
                  className={`relative w-12 h-7 rounded-full transition-colors cursor-pointer ${
                    weekendEnabled ? 'bg-gold-500' : 'bg-brown-200'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-sm transition-transform ${
                      weekendEnabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {/* 预览 */}
              <div className="bg-brown-50 rounded-lg p-3.5 space-y-1.5">
                <p className="text-brown-600 text-xs font-medium">当前设置预览</p>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-brown-700">
                    开盘：<span className="font-semibold text-brown-900">{openTime}</span>
                  </span>
                  <span className="text-brown-300">|</span>
                  <span className="text-brown-700">
                    闭盘：<span className="font-semibold text-brown-900">{closeTime}</span>
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-brown-700">
                    周末：
                    <span className={`font-semibold ${weekendEnabled ? 'text-green-600' : 'text-red-500'}`}>
                      {weekendEnabled ? '允许交易' : '休市'}
                    </span>
                  </span>
                </div>
              </div>
            </>
          )}

          {/* 保存按钮 */}
          <div className="pt-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full sm:w-auto px-6 py-2.5 rounded-lg bg-gold-500 text-brown-950 text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-gold-600 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <span className="w-4 h-4 border-2 border-brown-950/30 border-t-brown-950 rounded-full animate-spin"></span>
                  保存中...
                </>
              ) : saved ? (
                <>
                  <i className="ri-check-line"></i>
                  已保存
                </>
              ) : (
                <>
                  <i className="ri-save-line"></i>
                  保存设置
                </>
              )}
            </button>
          </div>
        </div>
      </SectionCard>
    </div>
  );
}